import { PriceTypeEnum, UnitAlert, UnitAlertParameter } from '@mitsubishi/shared/data-access';

/** The price levels the backend compares (APPLYME-6315): price list, sell-in and local; customer is never compared. */
export type UnitAlertPriceLevel = PriceTypeEnum.basePrice | PriceTypeEnum.sellIn | PriceTypeEnum.local;

export const UNIT_ALERT_PRICE_LEVELS: UnitAlertPriceLevel[] = [
  PriceTypeEnum.basePrice,
  PriceTypeEnum.sellIn,
  PriceTypeEnum.local,
];

export interface UnitAlertAccessory {
  configuratorCode: string;
  /** Resource id of the accessory name */
  name: string | number | undefined;
  crmCode: string;
  /** STD, UR, RFQ, NA, CFG; null for an optional accessory. Absent on price type changes, whose subject it is */
  priceType?: string | null;
  /** Only on replacements and removals; null for a missing level, never 0 */
  prices?: Record<UnitAlertPriceLevel, number | null>;
}

export interface UnitAlertPriceChange {
  level: UnitAlertPriceLevel;
  before: number | null;
  after: number | null;
}

export type UnitAlertRow =
  | { kind: 'MESSAGE'; message: string; parameters: UnitAlertParameter[] }
  | { kind: 'ACCESSORY_REPLACED'; message: string; removed: UnitAlertAccessory[]; added: UnitAlertAccessory[] }
  | { kind: 'ACCESSORY_REMOVED'; message: string; removed: UnitAlertAccessory[] }
  | {
      kind: 'PRICE_TYPE_CHANGED';
      message: string;
      accessory: UnitAlertAccessory;
      before: string | null;
      after: string | null;
    }
  | { kind: 'PRICE_CHANGED'; message: string; accessory: UnitAlertAccessory; changes: UnitAlertPriceChange[] }
  | {
      kind: 'WARRANTY_REMOVED';
      message: string;
      warrantyCode: string;
      prices: Record<UnitAlertPriceLevel, number | null>;
    };

/** Configurator code, name, CRM code */
const TRIPLE_SIZE = 3;
/** The triple and the price type */
const ACCESSORY_SIZE = TRIPLE_SIZE + 1;
const ACCESSORY_WITH_PRICES_SIZE = ACCESSORY_SIZE + UNIT_ALERT_PRICE_LEVELS.length;

const stringValue = (parameter: UnitAlertParameter | undefined): string =>
  parameter?.value == null ? '' : String(parameter.value);

const numberValue = (parameter: UnitAlertParameter | undefined): number | null =>
  typeof parameter?.value === 'number' ? parameter.value : null;

function toTriple(parameters: UnitAlertParameter[], index: number): UnitAlertAccessory {
  return {
    configuratorCode: stringValue(parameters[index]),
    name: parameters[index + 1]?.value ?? undefined,
    crmCode: stringValue(parameters[index + 2]),
  };
}

function toAccessory(parameters: UnitAlertParameter[], index: number, withPrices: boolean): UnitAlertAccessory {
  const accessory: UnitAlertAccessory = {
    ...toTriple(parameters, index),
    priceType: stringValue(parameters[index + 3]) || null,
  };
  if (withPrices) {
    accessory.prices = {
      [PriceTypeEnum.basePrice]: numberValue(parameters[index + 4]),
      [PriceTypeEnum.sellIn]: numberValue(parameters[index + 5]),
      [PriceTypeEnum.local]: numberValue(parameters[index + 6]),
    };
  }
  return accessory;
}

/** The accessories (with prices) from `start` to `end`, a whole number of them */
function toAccessories(parameters: UnitAlertParameter[], start: number, end: number): UnitAlertAccessory[] {
  const accessories: UnitAlertAccessory[] = [];
  for (let index = start; index + ACCESSORY_WITH_PRICES_SIZE <= end; index += ACCESSORY_WITH_PRICES_SIZE) {
    accessories.push(toAccessory(parameters, index, true));
  }
  return accessories;
}

/**
 * Turns an alert into a detail row (APPLYME-6315). The number of accessories varies (multi-answer questions), so the
 * row is composed by type and not with a positional template; parameters in an unexpected shape fall back to the
 * plain message, never to an exception.
 *
 * - ACCESSORY_REPLACED: N, N accessories out, the accessories in up to the end (7 parameters each: configurator code,
 *   name, CRM code, price type, price list, sell-in, local)
 * - ACCESSORY_REMOVED: N, N accessories out (7 parameters each)
 * - PRICE_TYPE_CHANGED: configurator code, name, CRM code, type before, type after
 * - PRICE_CHANGED / PRICE_INCREASED: configurator code, name, CRM code, price type, then before/after of price list,
 *   sell-in, local
 * - WARRANTY_REMOVED (APPLYMESD-2794): warranty period code, then price list, sell-in, local before the removal
 */
export function toUnitAlertRow(alert: UnitAlert): UnitAlertRow {
  const { message } = alert;
  const parameters = alert.parameters ?? [];
  const fallback: UnitAlertRow = { kind: 'MESSAGE', message, parameters };

  switch (alert.type) {
    case 'ACCESSORY_REPLACED':
    case 'ACCESSORY_REMOVED': {
      const removedCount = numberValue(parameters[0]);
      const removedEnd = 1 + (removedCount ?? 0) * ACCESSORY_WITH_PRICES_SIZE;
      if (removedCount == null || removedCount < 1 || removedEnd > parameters.length) {
        return fallback;
      }
      const removed = toAccessories(parameters, 1, removedEnd);
      if (alert.type === 'ACCESSORY_REMOVED') {
        return { kind: 'ACCESSORY_REMOVED', message, removed };
      }
      return {
        kind: 'ACCESSORY_REPLACED',
        message,
        removed,
        added: toAccessories(parameters, removedEnd, parameters.length),
      };
    }
    case 'PRICE_TYPE_CHANGED':
      if (parameters.length < TRIPLE_SIZE + 2) {
        return fallback;
      }
      return {
        kind: 'PRICE_TYPE_CHANGED',
        message,
        accessory: toTriple(parameters, 0),
        before: parameters[3].value == null ? null : String(parameters[3].value),
        after: parameters[4].value == null ? null : String(parameters[4].value),
      };
    case 'PRICE_CHANGED':
    case 'PRICE_INCREASED': {
      if (parameters.length < ACCESSORY_SIZE + UNIT_ALERT_PRICE_LEVELS.length * 2) {
        return fallback;
      }
      const changes = UNIT_ALERT_PRICE_LEVELS.map((level, i) => ({
        level,
        before: numberValue(parameters[ACCESSORY_SIZE + i * 2]),
        after: numberValue(parameters[ACCESSORY_SIZE + i * 2 + 1]),
      })).filter(change => change.before != null || change.after != null);
      return { kind: 'PRICE_CHANGED', message, accessory: toAccessory(parameters, 0, false), changes };
    }
    case 'WARRANTY_REMOVED':
      if (parameters.length < 1 + UNIT_ALERT_PRICE_LEVELS.length) {
        return fallback;
      }
      return {
        kind: 'WARRANTY_REMOVED',
        message,
        warrantyCode: stringValue(parameters[0]),
        prices: {
          [PriceTypeEnum.basePrice]: numberValue(parameters[1]),
          [PriceTypeEnum.sellIn]: numberValue(parameters[2]),
          [PriceTypeEnum.local]: numberValue(parameters[3]),
        },
      };
    default:
      return fallback;
  }
}
